import {
  getProjectKey,
  getProjectsKey,
  ProjectModel,
  useAddInvoice,
} from 'api-hooks/projects';
import { SaleModel } from 'api-hooks/sale/common';
import { PlusCircleSVG, TrashSVG } from 'common/assets';
import formSetErrors from 'common/helpers/form-setError';
import { queryClient } from 'common/repositories/query-client';
import stylesColors from 'common/styles/styles.colors';
import { themedStyled } from 'common/styles/styletron.theme';
import DrawerHeader from 'components/common/drawer/drawer-header';
import { DrawerBody } from 'components/common/drawer/styles';
import notification from 'components/common/notification';
import { Form, FormState, Input } from 'components/elements';
import { Button, IconButton } from 'components/elements/buttons';
import {
  FullContainer,
  SectionContainer,
  SeparatorHorizontal,
  SeparatorVertical,
} from 'components/styles';
import useYupValidationResolver from 'hooks/use-yup-validation-resolver';
import CustomerSelectInput from 'legacy-modules/master/customer/components/select-input';
import SaleSelectInput from 'legacy-modules/sales/component/select-input';
import { CardSection } from 'legacy-modules/styles';
import useTranslation from 'next-translate/useTranslation';
import React from 'react';
import { useFieldArray, useForm, useFormContext } from 'react-hook-form';
import * as Yup from 'yup';

type ItemFormType = {
  id?: string;
  saleRefId: string | null;
  date?: Date;
  code?: string;
  description?: string;
  customerId?: string;
};

type FormType = {
  items: ItemFormType[];
};
interface Props {
  onClose: () => void;
  data?: ItemFormType;
  project?: ProjectModel;
}

const itemSchema = Yup.object().shape({
  saleRefId: Yup.lazy((value) => {
    switch (typeof value) {
      case 'string':
        return Yup.string().nullable();
      default:
        return Yup.mixed();
    }
  }),
});

const yupSchema = Yup.object()
  .shape({
    items: Yup.array().of(itemSchema.required()).required(),
  })
  .required();

function Item(props: { name?: string; renderDelete?: React.ReactNode }) {
  const { t } = useTranslation();

  const { setValue } = useFormContext();

  return (
    <CardSection>
      <FullContainer>
        <SectionContainer $style={{ alignItems: 'center' }}>
          <InvoiceContainer>
            <SaleSelectInput
              name={`${props.name}.saleRefId`}
              required
              onAfterChange={(params) => {
                if (params?.value?.[0]?.extra) {
                  const _value = params.value[0].extra as SaleModel;
                  setValue(`${props.name}.customerId`, _value.customer.id);
                  setValue(
                    `${props.name}.date`,
                    new Date(_value.transactionAt),
                  );
                  setValue(`${props.name}.code`, _value.code);
                  setValue(`${props.name}.description`, _value.description);
                }
              }}
            />
          </InvoiceContainer>
          {props.renderDelete}
        </SectionContainer>
      </FullContainer>
      <FullContainer>
        <SectionContainer>
          <ItemHalfContainer>
            <Input
              name={`${props.name}.date`}
              type="date-time"
              label={t('common:date')}
              disabled
            />
          </ItemHalfContainer>
          <ItemHalfContainer>
            <Input
              name={`${props.name}.code`}
              type="text"
              label={t('common:code')}
              disabled
            />
          </ItemHalfContainer>
        </SectionContainer>
      </FullContainer>
      <FullContainer>
        <SectionContainer>
          <ItemHalfContainer>
            <Input
              name={`${props.name}.description`}
              type="text-area"
              label={t('common:description')}
              disabled
            />
          </ItemHalfContainer>
          <ItemHalfContainer>
            <CustomerSelectInput name={`${props.name}.customerId`} disabled />
          </ItemHalfContainer>
        </SectionContainer>
      </FullContainer>
    </CardSection>
  );
}

function Items() {
  const { t } = useTranslation();
  const { control } = useFormContext<FormType>();
  const { fields, append, remove } = useFieldArray({
    control,
    name: 'items',
    keyName: 'customId',
  });

  const onAdd = () =>
    append({
      saleRefId: null,
    });

  const onRemove = (index: number) => remove(index);

  return (
    <FormState>
      {({ editable }) => (
        <>
          {fields.map((field, index) => (
            <>
              <Item
                key={field.customId}
                name={`items.${index}`}
                renderDelete={
                  editable &&
                  index !== 0 && (
                    <IconButton
                      buttonType="tertiary"
                      overrides={{
                        Root: {
                          style: {
                            height: '100%',
                            ':hover': {
                              backgroundColor: stylesColors.redDefault,
                            },
                          },
                        },
                      }}
                    >
                      <TrashSVG
                        color={stylesColors.redDefault}
                        width="24"
                        onClick={() => onRemove(index)}
                      />
                    </IconButton>
                  )
                }
              />
              <SeparatorVertical $margin={16} />
            </>
          ))}
          {editable && (
            <div>
              <Button
                buttonType="tertiary"
                startEnhancer={(color, size) => (
                  <PlusCircleSVG color={color} size={size} />
                )}
                onClick={onAdd}
              >
                {t('common:add')}
              </Button>
            </div>
          )}
        </>
      )}
    </FormState>
  );
}

export default function AddInvoiceFormPanel(props: Props) {
  const { t } = useTranslation();

  const { mutateAsync } = useAddInvoice();

  const defaultValues = React.useMemo<FormType>(() => {
    return {
      items: [
        {
          saleRefId: null,
        },
      ],
    };
  }, []);

  const resolver = useYupValidationResolver(yupSchema);
  const methods = useForm<FormType>({
    resolver,
    defaultValues,
    mode: 'onChange',
  });

  const onSubmit = React.useCallback(
    async (values) => {
      try {
        values.items = values.items.map((item) => ({
          saleRefId: item.saleRefId.id,
        }));
        const result = await mutateAsync({
          projectId: props.project?.id!,
          body: values,
        });
        notification.success({ message: result?.message });

        queryClient.refetchQueries([getProjectsKey()[0]]);
        queryClient.refetchQueries(getProjectKey({ id: props.project?.id! }));

        props.onClose();
      } catch (e) {
        if (e.errors) {
          formSetErrors(e.errors, methods.setError);
        }
        notification.error({ message: e.message });
      }
    },
    [methods.setError, mutateAsync, props],
  );
  return (
    <>
      <DrawerHeader
        onClose={props.onClose}
        title={t('common:add_extra', { extra: t('common:invoice') })}
      />
      <DrawerBody>
        <Form methods={methods} onSubmit={onSubmit}>
          <Items />
          <FullContainer style={{ display: 'flex', justifyContent: 'end' }}>
            <Button buttonType="tertiary" onClick={props.onClose}>
              {t('common:cancel')}
            </Button>
            <SeparatorHorizontal $margin={8} />
            <Input type="submit" />
          </FullContainer>
        </Form>
      </DrawerBody>
    </>
  );
}

const ItemHalfContainer = themedStyled('div', ({ $theme }) => ({
  width: '100%',
  [`${$theme.mediaQuery.medium}`]: {
    width: '47%',
  },
}));

const InvoiceContainer = themedStyled('div', {
  width: '90%',
});
